import { formatPrice, removeCartLineItem, setPreorderOnCartLevel, hasPreorderItems } from "../helpers";
import { updateMiniCartItemCount } from "./header";

/** @param {HTMLElement} baseElement */
export function init(baseElement) {
  const miniCartOverlay = baseElement.querySelector(".mini-cart-overlay");
  if (miniCartOverlay instanceof HTMLElement) {
    setupOverlayClick(miniCartOverlay);
  }

  const backToShopButton = baseElement.querySelector(".back-to-shop-cta");
  if (backToShopButton instanceof HTMLElement) {
    backToShopButton.addEventListener("click", (e) => {
      e.preventDefault();
      closeMinicart();
    });
  }
}

function setupOverlayClick(element) {
  element.addEventListener("click", (e) => {
    if (e.target.id == "mini-cart-overlay") {
      closeMinicart();
    }
  });
}
/* function setupCloseButton(baseElement) {
  baseElement.onclick = closeMinicart;
} */

/**
 *
 * @returns {HTMLElement} mini-cart section element
 */
export function getMiniCart() {
  return document.getElementById("shopify-section-mini-cart");
}

/**
 * opens the minicart
 */
export function openMinicart() {
  const miniCart = getMiniCart();
  const overlay = miniCart?.querySelector(".mini-cart-overlay");
  const container = miniCart?.querySelector(".mini-cart-container");

  if (container instanceof HTMLElement) {
    clearMiniCartLines(miniCart);
    overlay.classList.add("is-open");
    container.classList.remove("translate-x-full");
    populateMiniCart(miniCart);
  }
}

/**
 * closes the minicart
 */
export function closeMinicart() {
  const miniCart = getMiniCart();
  const overlay = miniCart?.querySelector(".mini-cart-overlay");
  const container = miniCart?.querySelector(".mini-cart-container");

  if (container instanceof HTMLElement) {
    overlay.classList.remove("is-open");
    container.classList.add("translate-x-full");
  }
}

/** @param {HTMLElement} baseElement */
function clearMiniCartLines(baseElement) {
  const container = baseElement.querySelector(".mini-cart-content");
  if (!(container instanceof HTMLElement)) return;

  const lines = container.querySelectorAll(".mini-cart-line");
  lines.forEach((line) => container.removeChild(line));
}

/**
 * Fetches and renders mini cart data
 * @param {HTMLElement} baseElement */
export async function populateMiniCart(baseElement) {
  // Show spinner
  const loading = baseElement.querySelector(".mini-cart-loading");
  loading?.classList?.remove("hidden");

  // Fetch cart data
  const response = await fetch(window.Shopify.routes.root + "cart.js");
  const data = await response.json();

  if(data.attributes && data.attributes['_is_preorder']) {
    const preorder = data.items.find(item => item.properties && item.properties['_is_preorder']);

    if(preorder){
      setPreorderOnCartLevel(true);
    } else {
      setPreorderOnCartLevel(false);
    }
  }

  // Hide spinner
  loading?.classList?.add("hidden");

  // Gather data slice for renders
  const cartSlice = {
    currencyCode: data["currency"],
    totalPrice: data["total_price"],
    //totalDiscount: data["total_discount"],
  };

  const itemCount = data["item_count"];
  if (typeof itemCount !== "number") return;

  // Display number of items between parenthesis after the BAG.
  const titleLinesTotal = baseElement.querySelector(
    ".mini-cart-items-quantity"
  );
  if (titleLinesTotal instanceof HTMLElement) {
    titleLinesTotal.textContent =
      itemCount === 0 ? "(0)" : "(" + itemCount + ")";
  }

  const footer = baseElement.querySelector(".mini-cart-footer");

  if (itemCount === 0) {
    // Display empty bag message
    const title = baseElement.querySelector(".mini-cart-title");
    if (title instanceof HTMLElement) {
      title.textContent = "Your bag is empty";
    }
    // Hide footer
    if (footer instanceof HTMLElement) {
      footer.classList.add("hidden");
    }
    return;
  }

  // Display footer
  if (footer instanceof HTMLElement) {
    footer.classList.remove("hidden");
  }

  if (typeof cartSlice.totalPrice === "number") {
    const subTotal = baseElement.querySelector(".mini-cart-subtotal");
    if (subTotal instanceof HTMLElement) {
      subTotal.textContent = formatPrice(
        cartSlice.totalPrice / 100,
        undefined, // browser default locale
        cartSlice.currencyCode
      );
    }
  }

  const lineItemTemplate = baseElement.querySelector(
    ".mini-cart-line-template"
  );
  if (!(lineItemTemplate instanceof HTMLTemplateElement)) return;

  const items = data["items"];
  if (!(items instanceof Array)) return;

  for (let item of items) {
    // Gather data slices for render
    const cartItemSlice = {
      key: item["key"],
      image: item["image"],
      title: item["product_title"],
      quantity: item["quantity"],
      priceEach: item["final_price"],
      variantName: item["variant_title"],
    };

    // Clone template element, keep it for reuse.
    const lineItemClone = lineItemTemplate.cloneNode(true);
    if (!(lineItemClone instanceof HTMLElement)) return;

    // Reparent template content into a div
    const lineItem = document.createElement("div");
    lineItem.classList.value = lineItemClone.classList.value;
    lineItem.innerHTML = lineItemClone.innerHTML;

    // Update image source URL
    if (typeof cartItemSlice.image === "string") {
      const lineItemImage = lineItem.querySelector(".mini-cart-line-image");
      if (lineItemImage instanceof HTMLImageElement) {
        lineItemImage.src = cartItemSlice.image;
      }
    }

    if (typeof cartItemSlice.title === "string") {
      const lineItemTitle = lineItem.querySelector(".mini-cart-line-title");
      if (lineItemTitle instanceof HTMLElement) {
        lineItemTitle.textContent = cartItemSlice.title;
      }
    }

    if (typeof cartItemSlice.quantity === "number") {
      const lineItemQty = lineItem.querySelector(".mini-cart-line-quantity");
      if (lineItemQty instanceof HTMLElement) {
        lineItemQty.textContent = cartItemSlice.quantity.toString();
      }
    }

    if (typeof cartItemSlice.priceEach === "number") {
      const lineItemPrice = lineItem.querySelector(".mini-cart-line-price");
      if (lineItemPrice instanceof HTMLElement) {
        lineItemPrice.textContent = formatPrice(
          cartItemSlice.priceEach / 100,
          undefined, // browser default locale
          cartSlice.currencyCode
        );
      }
    }

    if (typeof cartItemSlice.variantName === "string") {
      const lineItemSize = lineItem.querySelector(".mini-cart-line-size");
      if (lineItemSize instanceof HTMLElement) {
        lineItemSize.textContent = cartItemSlice.variantName;
      }
    }

    if (typeof cartItemSlice.key === "string") {
      const lineItemRemove = lineItem.querySelector(".mini-cart-line-remove");

      if (lineItemRemove instanceof HTMLElement) {
        lineItemRemove.onclick = async () => {
          let data;
          try {
            data = await removeCartLineItem(cartItemSlice.key);
            if(!hasPreorderItems(data)){
              await setPreorderOnCartLevel(false);
            };
          } catch (err) {
            console.error(err);
          }
          if (typeof data === "object" && "items" in data) {
            // Success!
            clearMiniCartLines(baseElement);
            populateMiniCart(baseElement);
            // Header
            updateMiniCartItemCount();
          } else {
            const message = data?.description || "Something went wrong";
            window.alert(message);
          }
        };
      }
    }

    // Change class to differentiate from the template
    lineItem.classList.replace("mini-cart-line-template", "mini-cart-line");

    const container = baseElement.querySelector(".mini-cart-content");
    if (container instanceof HTMLElement) {
      container.appendChild(lineItem);
    }
  }
}
